import { DEFAULT_OP_DEADLINE_MS } from './lib/admin-lifecycle.js';

const routes = [];

function compilePattern(pattern) {
  const keys = [];
  const regexSrc = pattern.replace(/:(\w+)/g, (_, key) => {
    keys.push(key);
    return '([^/]+)';
  });
  return { regex: new RegExp(`^${regexSrc}/?$`), keys };
}

// register(pattern, screenName, handler, meta) — `meta = { group, title, action? }`
// drives the header breadcrumb eyebrow (group), screen title, and the topbar
// action slot (§4). `meta` is optional so a bare 3-arg registration still works.
export function register(pattern, screenName, handler, meta = {}) {
  const { regex, keys } = compilePattern(pattern);
  routes.push({ pattern, keys, regex, screenName, handler, meta });
}

function match(pathname) {
  for (const route of routes) {
    const m = pathname.match(route.regex);
    if (m) {
      const params = {};
      try {
        route.keys.forEach((k, i) => { params[k] = decodeURIComponent(m[i + 1]); });
      } catch {
        // A malformed %-escape in a captured segment (e.g. `/admin/users/%`) makes
        // decodeURIComponent throw URIError. Do NOT let it propagate: render() calls
        // match() AFTER draining the previous screen, so a throw would reject the
        // navigation and strand a torn-down-but-visible dead screen (plus an
        // unhandled rejection). Treat the bad URL as unmatched → the no-route
        // fallback (dashboard) renders cleanly.
        continue;
      }
      return { route, params };
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Teardown transaction (Task 1.5)
//
// TEARDOWN CALLBACKS ARE SYNCHRONOUS by contract (abort / gen.bump /
// removeEventListener / cancelAnimationFrame — all sync). So drain is fully
// synchronous and its `ok` is accurate the instant it returns; no thenable is
// awaited and none can produce an unhandled rejection. (An in-flight fetch is
// cancelled by a sync `op.abort(new DOMException('navigation', 'AbortError'))`
// callback — not awaited here.)
//
// The router holds the OPEN navigation's transaction. Each navigation opens a
// fresh one and drains the previous BEFORE matching/invoking the next handler —
// so cleanup fires on EVERY path, including an unmatched navigation. Draining
// also SUPERSEDES the previous navigation, releasing any render() still awaiting
// its (possibly hung) handler.
// ─────────────────────────────────────────────────────────────────────────────
const LATE_CAP = 100;
// Router backstop, DERIVED from the screen operation deadline so it stays strictly
// GREATER by construction (a compliant slow load always resolves before this; only
// a genuinely stuck handler hits it). Deriving it — rather than a second hard-coded
// literal — means bumping the operation deadline can never silently invert the
// invariant and misclassify compliant slow loads as timeouts across every screen.
const HANDLER_DEADLINE_MS = DEFAULT_OP_DEADLINE_MS + 5000;
const SUPERSEDED = Symbol('superseded');   // race sentinel — the NEXT navigation drained this txn
const TIMED_OUT = Symbol('handler-timeout'); // race sentinel — the absolute backstop fired

function newTxn() {
  let supersede;                           // resolve THIS navigation's supersession promise when it is drained by the next
  const superseded = new Promise((res) => { supersede = () => res(SUPERSEDED); });
  return { list: [], closed: false, settled: false, late: [], lateDropped: 0, supersede, superseded };
}

// The OPEN navigation's teardown transaction (owns its own late-cleanup failures).
// Named `currentTxn` (not `current`) so it never shadows updateNavHighlight's own
// pathname-scoped `current`.
let currentTxn = newTxn();

function deadline(ms) {                     // { promise, cancel } — the CANCELABLE backstop timer (cancel() clears it so no timer leaks per completed navigation)
  let t;
  const promise = new Promise((res) => { t = setTimeout(() => res(TIMED_OUT), ms); });
  return { promise, cancel: () => clearTimeout(t) };
}

// A cleanup that arrives AFTER its txn drained is attributed to THAT txn
// (transaction ownership) — not a shared global. Bounded, with a VISIBLE overflow
// count (never silently discarded); merged into the navigation result until it
// settles, then reported via an INJECTABLE non-throwing sink (the superseded
// navigation still owns the failure).
const defaultLateCleanupSink = (txn, error) =>
  console.error('[admin/router] late teardown failed', { error, lateDropped: txn.lateDropped });
let lateCleanupSink = defaultLateCleanupSink; // observable by default; tests/telemetry may inject a replacement

export function setLateCleanupSink(fn) {
  lateCleanupSink = typeof fn === 'function' ? fn : defaultLateCleanupSink;
}

function recordLateOn(txn, e) {
  if (txn.late.length < LATE_CAP) txn.late.push(e); else txn.lateDropped++; // BEFORE the navigation result settles → these are folded INTO it (see render)
  if (txn.settled) { try { lateCleanupSink?.(txn, e); } catch { /* a bad sink never interrupts cleanup */ } } // AFTER return → can only be REPORTED (with the overflow count) via the sink
}

function runOne(fn, sink) {                 // run ONE SYNC teardown; fold its {ok:false,errors} OR thrown error (falsy throws included)
  try {
    const r = fn();
    if (r && typeof r.then === 'function') {
      Promise.resolve(r).catch(() => {}); // CONSUME a stray rejection so a promise-returning teardown can never become an unhandledrejection…
      sink(new Error('teardown returned a promise: teardown callbacks MUST be synchronous')); // …while STILL recording the sync contract violation (not fail-open, not ignored)
      return;
    }
    if (r && r.ok === false) {
      sink(...(Array.isArray(r.errors) && r.errors.length
        ? r.errors
        : [r.error ?? new Error('teardown reported ok:false with no errors')])); // NEVER fail-open on {ok:false,errors:[]}
    }
  } catch (e) { sink(e); }
}

function drain(txn) {
  txn.closed = true;                        // any onTeardown BOUND to this txn now runs late (see makeCtx)
  const errors = [];
  for (const fn of txn.list.splice(0)) runOne(fn, (...es) => errors.push(...es)); // detach (splice) → never double-run; SYNC
  txn.supersede();                          // RELEASE any render() still awaiting THIS navigation's handler — a hung handler no longer waits for the 20s backstop
  return { ok: errors.length === 0, errors }; // accurate the moment it returns
}

// makeCtx BINDS onTeardown to THIS navigation's txn — it does NOT read the global
// currentTxn at call time, so an older async handler registering LATE targets ITS
// (now-closed) txn and runs late, never inserting into the newer navigation.
function makeCtx(txn, params, root) {
  return {
    root, params,
    onTeardown(fn) {
      if (txn.closed) {                     // late (superseded async handler) → run NOW (sync); failures attributed to THIS txn, bounded, merged-then-sinked — never a shared global
        runOne(fn, (...es) => es.forEach((e) => recordLateOn(txn, e)));
        return () => {};
      }
      txn.list.push(fn);
      return () => { const i = txn.list.indexOf(fn); if (i >= 0) txn.list.splice(i, 1); }; // unregister targets THIS txn ONLY
    },
  };
}

// Set the header breadcrumb eyebrow (meta.group), screen title (meta.title), and
// action slot (meta.action, or clear) SYNCHRONOUSLY. Pass meta=null to RESET all
// three (unmatched navigation). Called on every navigation, so re-entering a
// screen overwrites rather than appends.
function applyRouteMeta(meta, ctx) {
  const eyebrow = document.querySelector('.crumb-eyebrow');
  const title = document.querySelector('.crumb-title');
  const slot = document.getElementById('admin-action-slot');
  if (eyebrow) eyebrow.textContent = (meta && meta.group) || '';
  if (title) title.textContent = (meta && meta.title) || '';
  if (slot) {
    slot.textContent = '';                  // reset the slot first (clears any prior action node/text)
    const action = meta && meta.action;
    if (action != null) {
      let node = action;
      if (typeof action === 'function') {
        try { node = action(ctx); }
        catch (e) { node = null; console.error('[admin/router] action factory threw:', e); }
      }
      if (node != null) {
        if (typeof node === 'string') slot.textContent = node;
        else if (typeof node.nodeType === 'number') slot.appendChild(node);
        else slot.textContent = String(node);
      }
    }
  }
}

// Reset the shell's scroll for a freshly-shown screen. The admin scrolls the
// `.content` pane (#admin-root) INTERNALLY — the window itself does not scroll
// (shell.css `.app` is a fixed-viewport grid with `overflow:hidden`; `.content`
// owns `overflow-y:auto`) — so the browser's native scroll restoration, which
// only manages the document, never touches it. EVERY navigation-commit path
// (forward via navigate(), Back/Forward via the popstate handler) routes
// through here so each destination screen starts at the top, matching the
// admin's long-standing "reset to top on navigation" behaviour instead of
// inheriting the outgoing screen's scrollTop. window.scrollTo stays as a
// harmless fallback for any non-shell page. Callers invoke this BEFORE render()
// so the systems handler's single post-render rAF scrollIntoView is not
// clobbered (verified).
function resetShellScroll() {
  const contentPane = document.getElementById('admin-root');
  if (contentPane) contentPane.scrollTop = 0;
  window.scrollTo(0, 0);
}

export function navigate(path, { replace = false } = {}) {
  if (replace) history.replaceState(null, '', path);
  else history.pushState(null, '', path);
  resetShellScroll();
  return render();                           // callers may ignore the promise; tests await the navigation result
}

// render() resolves with a structured report of the navigation:
//   { matched, screen, ok, handler, errors, outgoing, late, lateDropped }
// where `handler` is the discriminated outcome — the real handler value, or
// { kind:'timeout' } / { kind:'cancelled', reason:'navigation' } / { kind:'error', error }.
export async function render() {
  // Drain (and SUPERSEDE) the PREVIOUS navigation before matching — fires on every path.
  const outgoing = drain(currentTxn);
  const txn = (currentTxn = newTxn());

  const root = document.getElementById('admin-root');
  if (!root) {
    txn.settled = true;
    return { matched: false, screen: null, ok: true, noRoot: true, handler: null, errors: [], outgoing, late: [], lateDropped: 0 };
  }

  const found = match(location.pathname);
  const sections = root.querySelectorAll('[data-screen]');
  for (const s of sections) s.hidden = true;

  if (!found) {
    const first = root.querySelector('[data-screen="dashboard"]');
    if (first) first.hidden = false;
    applyRouteMeta(null, null);             // reset the header (no route)
    updateNavHighlight();
    txn.settled = true;
    return { matched: false, screen: null, ok: true, handler: null, errors: [], outgoing, late: txn.late.slice(), lateDropped: txn.lateDropped };
  }

  const target = root.querySelector(`[data-screen="${found.route.screenName}"]`);
  if (target) target.hidden = false;

  const ctx = makeCtx(txn, found.params, root);
  // Reveal + nav/header feedback SYNCHRONOUSLY, before awaiting a possibly-slow loader.
  applyRouteMeta(found.route.meta, ctx);
  updateNavHighlight();

  const errors = [];
  let handlerResult;
  let abnormal = false;                     // SUPERSEDED / TIMED_OUT / throw → drain the just-registered teardowns now

  const dl = deadline(HANDLER_DEADLINE_MS);
  let raced, threw = false, thrownErr;
  try {
    // Three-way race: the handler value vs THIS txn being superseded vs the backstop.
    // .finally(dl.cancel) clears the losing timer whichever branch wins.
    raced = await Promise.race([
      Promise.resolve(found.route.handler(found.params, ctx)),
      txn.superseded,
      dl.promise,
    ]).finally(() => dl.cancel());
  } catch (err) {
    dl.cancel();                            // sync throw from handler(...) never attaches .finally → clear here (idempotent on an async rejection)
    threw = true;
    thrownErr = err;
  }

  if (threw) {
    handlerResult = { kind: 'error', error: thrownErr };
    errors.push(thrownErr);
    abnormal = true;
  } else if (raced === SUPERSEDED) {
    handlerResult = { kind: 'cancelled', reason: 'navigation' };
    abnormal = true;
  } else if (raced === TIMED_OUT) {
    handlerResult = { kind: 'timeout' };
    abnormal = true;
  } else {
    handlerResult = raced;
    if (raced && typeof raced === 'object' && raced.ok === false) {
      errors.push(...(Array.isArray(raced.errors) ? raced.errors : [])); // a partial failure is folded — NOT full success
    }
  }

  if (abnormal) {
    // Release the resources the handler just registered. On SUPERSEDED this txn was
    // already drained by the next navigation, so this is a harmless no-op; on
    // TIMED_OUT / throw it is the drain that actually runs the just-registered teardowns.
    const d = drain(txn);
    if (!d.ok) errors.push(...d.errors);
  }

  txn.settled = true;                       // from here, a late cleanup can only be REPORTED via the sink (see recordLateOn)

  const clean = !threw
    && raced !== SUPERSEDED
    && raced !== TIMED_OUT
    && !(handlerResult && typeof handlerResult === 'object' && handlerResult.ok === false);

  return {
    matched: true,
    screen: found.route.screenName,
    ok: clean && errors.length === 0 && txn.late.length === 0 && txn.lateDropped === 0,
    handler: handlerResult,
    errors,
    outgoing,
    late: txn.late.slice(),                 // late cleanups that arrived DURING this render, folded into the result
    lateDropped: txn.lateDropped,
  };
}

export function updateNavHighlight() {
  const current = location.pathname.replace(/\/$/, '') || '/';
  const links = document.querySelectorAll('.admin-nav-link');

  let bestTarget = null;
  let bestLen = 0;
  for (const link of links) {
    const target = link.getAttribute('data-route');
    if (!target) continue;
    const isMatch = target === '/admin'
      ? current === '/admin'
      : current === target || current.startsWith(target + '/');
    if (isMatch && target.length > bestLen) {
      bestTarget = target;
      bestLen = target.length;
    }
  }

  for (const link of links) {
    const isActive = link.getAttribute('data-route') === bestTarget;
    link.classList.toggle('active', isActive);
    // aria-current="page" (spec §3.8) — kept in lockstep with `.active` so a
    // screen reader (and the `.nav-item[aria-current="page"]` CSS hook) agree
    // with the sighted highlight on exactly one nav item at a time.
    if (isActive) link.setAttribute('aria-current', 'page');
    else link.removeAttribute('aria-current');
  }
}

let _clickHandler = null;
let _popstateHandler = null;

function attachLinkInterceptor() {
  if (_clickHandler) return;

  _clickHandler = (e) => {
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href) return;
    if (!href.startsWith('/admin')) return;
    if (a.target && a.target !== '_self') return;
    if (a.hasAttribute('download')) return;

    e.preventDefault();
    navigate(href);
  };
  // Back/Forward: reset the inner scroller too (browser scroll restoration only
  // manages the document, which no longer scrolls) so a traversed-to screen
  // never inherits the outgoing screen's scrollTop.
  _popstateHandler = () => { resetShellScroll(); render(); };

  document.addEventListener('click', _clickHandler);
  window.addEventListener('popstate', _popstateHandler);
}

export function start() {
  attachLinkInterceptor();
  return render();
}
